import { posthog } from './analytics';
import { getKeyValue, removeKeyValue, storeKeyValue } from './kvstore';
const DEBUG = false;
const MAX_AGE = 60 * 60 * 24 * 400; // 34560000 // 400 days (max age permitted by Safari in 2024)
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
function log(msg) {
    if (DEBUG)
        console.log(msg);
}
// returns whether a string is a valid UUID, loosely defined as 8-4-4-4-12 hex characters without strict validation
export const isValidUUID = (uuid) => {
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i;
    if (regex.test(uuid))
        return uuid;
    return null;
};
// stores a guid in session, local, cookie and indexedDb storage
export const storeUuid = async (key, value) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return false;
    const now = new Date().getTime();
    if (isValidUUID(value) === null) {
        log(`tried to store an invalid uuid: ${value}`);
        return false;
    }
    let success = false;
    try {
        sessionStorage.setItem(key, JSON.stringify({ value, now }));
        log(`sessionStorage[${key}] <- ${value}`);
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} in session storage: ${e}`);
        posthog.capture(`Error storing in session storage`, { key, error: e });
    }
    try {
        localStorage.setItem(key, JSON.stringify({ value, now }));
        log(`localStorage[${key}] <- ${value}`);
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} in local storage: ${e}`);
        posthog.capture(`Error storing in local storage`, { key, error: e });
    }
    try {
        const success2 = await storeKeyValue(key, JSON.stringify({ value, now }));
        log(`indexDb[${key}] <- ${value}`);
        success = success || success2;
    }
    catch (e) {
        console.log(`Error storing ${key} in indexedDb: ${e}`);
        posthog.capture(`Error storing in indexedDb`, { key, error: e });
    }
    try {
        document.cookie = `__HOST-${key}=${JSON.stringify({ value, now })}; path=/; Max-Age=${MAX_AGE}; SameSite=Strict; Secure;`;
        success = true;
    }
    catch (e) {
        console.log(`Error storing ${key} as cookie: ${e}`);
        posthog.capture(`Error storing in cookie`, { key, error: e });
    }
    return success;
};
// gets the stored guid from session, local, cookie and indexedDb storage
export const getUuid = async (key, latest) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return null;
    let value = null;
    let now = latest ? 0 : new Date().getTime();
    try {
        const str = sessionStorage.getItem(key);
        if (str) {
            const data = JSON.parse(str);
            if (data.value && data.now && ((latest && data.now > now) || data.now < now)) {
                const uuid = isValidUUID(data.value);
                if (uuid) {
                    value = uuid;
                    now = data.value;
                    log(`sessionStorage[${key}] -> ${value}`);
                }
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from session storage: ${e}`);
        posthog.capture(`Error getting from session storage`, { key, error: e });
    }
    try {
        const str = localStorage.getItem(key);
        if (str) {
            const data = JSON.parse(str);
            if (data.value && data.now && ((latest && data.now > now) || data.now < now)) {
                const uuid = isValidUUID(data.value);
                if (uuid) {
                    value = uuid;
                    now = data.value;
                    log(`localStorage[${key}] -> ${value}`);
                }
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from local storage: ${e}`);
        posthog.capture(`Error getting from local storage`, { key, error: e });
    }
    try {
        const str = await getKeyValue(key);
        if (str) {
            const data = JSON.parse(str);
            if (data.value && data.now && ((latest && data.now > now) || data.now < now)) {
                const uuid = isValidUUID(data.value);
                if (uuid) {
                    value = uuid;
                    now = data.value;
                    log(`indexDb[${key}] -> ${value}`);
                }
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from indexedDb storage: ${e}`);
        posthog.capture(`Error getting from indexedDb`, { key, error: e });
    }
    try {
        let data = window.document.cookie ?? '';
        if (data.startsWith('?'))
            data = data.slice(1);
        const items = data.split('; ');
        const item = items.find((p) => p.startsWith(`__HOST-${key}=`));
        const str = item?.split('=')[1];
        if (str) {
            const data = JSON.parse(str);
            if (data.value && data.now && ((latest && data.now > now) || data.now < now)) {
                const uuid = isValidUUID(data.value);
                if (uuid) {
                    value = uuid;
                    now = data.value;
                    log(`cookie[${key}] -> ${value}`);
                }
            }
        }
    }
    catch (e) {
        console.log(`Error getting ${key} from cookies: ${e}`);
        posthog.capture(`Error getting from cookie`, { key, error: e });
    }
    return value;
};
// removes the stored guid from session, local, cookie and indexedDb storage
export const clearUuid = async (key) => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined')
        return;
    try {
        sessionStorage.removeItem(key);
    }
    catch (e) {
        console.log(`Error removing ${key} from session storage: ${e}`);
        posthog.capture(`Error removing from session storage`, { key, error: e });
    }
    try {
        localStorage.removeItem(key);
    }
    catch (e) {
        console.log(`Error removing ${key} from local storage: ${e}`);
        posthog.capture(`Error removing from local storage: ${e}`, { key, error: e });
    }
    try {
        await removeKeyValue(key);
    }
    catch (e) {
        console.log(`Error getting ${key} from indexedDb storage: ${e}`);
        posthog.capture(`Error removing from indexedDb`, { key, error: e });
    }
    try {
        document.cookie = `__HOST-${key}=foobar; path=/; Max-Age=-99999999; SameSite=Strict; Secure;`;
    }
    catch (e) {
        console.log(`Error removing ${key} from cookies: ${e}`);
        posthog.capture(`Error removing from cookie storage`, { key, error: e });
    }
    await sleep(20);
    const value = await getUuid(key);
    if (value) {
        console.log(`Error clearing Uuid from storage`);
        posthog.capture(`Error clearing Uuid from storage`);
    }
};
// gets the stored guid from session, local, cookie and indexedDb storage
// and restores (refreshes) it into all storage locations
export const refreshUuid = async (key, latest) => {
    const value = await getUuid(key, latest);
    if (value)
        await storeUuid(key, value);
    return value;
};
